import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Trash2, MinusCircle, PlusCircle, ShoppingBag, AlertCircle, ArrowLeft, CreditCard, MapPin, Info } from 'lucide-react';
import { API_URL, API_URL_UPLOADS } from './config';

const Cart = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(state?.cart || []);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  const subtotal = cart.reduce((total, item) => total + item.selling_price * item.quantity, 0);
  const depositAmount = subtotal * 0.7; // 70% deposit
  const balanceAmount = subtotal * 0.3; // 30% balance

  const updateQuantity = (productId, change) => {
    setCart(prevCart =>
      prevCart.map(item =>
        item.product_id === productId
          ? { ...item, quantity: Math.max(1, item.quantity + change) }
          : item
      )
    );
  };

  const removeItem = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  };

  const handleCheckout = () => {
    navigate('/checkout', { state: { cart } });
  };

  return (
    <div className="min-vh-100 bg-light py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="d-flex align-items-center mb-4">
              <Link to="/" className="btn btn-link text-dark p-0 me-3">
                <ArrowLeft size={24} />
              </Link>
              <h2 className="mb-0">Shopping Cart</h2>
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-5 bg-white rounded shadow-sm">
                <ShoppingBag size={48} className="text-muted mb-3" />
                <h4>Your cart is empty</h4>
                <p className="text-muted mb-4">Looks like you haven't added anything to your cart yet.</p>
                <Link to="/" className="btn btn-warning px-4 py-2">
                  Continue Shopping
                </Link>
              </div>
            ) : (
              <div className="row g-4">
                <div className="col-md-8">
                  <div className="bg-white rounded shadow-sm p-4">
                    {cart.map(item => (
                      <div key={item.product_id} className="cart-item mb-4 pb-4 border-bottom">
                        <div className="d-flex gap-3">
                          <img
                            src={`${API_URL_UPLOADS}/${item.uploaded_product_image_ref}`}
                            alt={item.product_name}
                            className="rounded"
                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h5 className="mb-1">{item.product_name}</h5>
                                <p className="text-muted mb-1">Category: {item.category}</p>
                                <div className="text-primary fw-bold mb-2">
                                  ${item.selling_price.toFixed(2)}
                                </div>
                              </div>
                              <button
                                className="btn btn-link text-danger p-0"
                                onClick={() => removeItem(item.product_id)}
                              >
                                <Trash2 size={20} />
                              </button>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-outline-secondary btn-sm p-0 rounded-circle"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => updateQuantity(item.product_id, -1)}
                              >
                                <MinusCircle size={16} />
                              </button>
                              <span className="px-3">{item.quantity}</span>
                              <button
                                className="btn btn-outline-secondary btn-sm p-0 rounded-circle"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => updateQuantity(item.product_id, 1)}
                              >
                                <PlusCircle size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="bg-white rounded shadow-sm p-4">
                    <h5 className="mb-4">Order Summary</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Required Deposit (70%)</span>
                      <span className="text-primary fw-bold">${depositAmount.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-4">
                      <span>Balance on Delivery (30%)</span>
                      <span>${balanceAmount.toFixed(2)}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between mb-4">
                      <span className="fw-bold">Total</span>
                      <span className="fw-bold">${subtotal.toFixed(2)}</span>
                    </div>
                    <button
                      className="btn btn-warning w-100 py-2 mb-3"
                      onClick={handleCheckout}
                    >
                      Proceed to Checkout
                    </button>
                    <Link to="/" className="btn btn-outline-secondary w-100 py-2">
                      Continue Shopping
                    </Link>

                    <div className="mt-4">
                      <div className="alert alert-info" role="alert">
                        <div className="d-flex gap-2">
                          <Info size={20} />
                          <div>
                            <h6 className="mb-1">Payment Information</h6>
                            <p className="small mb-0">
                              In order to make cash payment for these products, visit our offices at 30 Samora Machael avenues, Nicoz diamond building, Harare.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <CreditCard size={20} className="text-muted" />
                        <span className="small">Secure payment process</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <MapPin size={20} className="text-muted" />
                        <span className="small">Available for pickup</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCheckoutModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center px-4 pb-5">
                <AlertCircle size={48} className="text-warning mb-3" />
                <h4>Contact XGo Team</h4>
                <p className="text-muted mb-4">
                  Thank you for Shopping with us, your order has been saved. Contact XGo team to finish processing
                  your purchase and get detailed payment and checkout instructions. A deposit of 70% is required, with the balance payable upon delivery/collection.
                </p>
                <button
                  className="btn btn-warning px-4"
                  onClick={() => setShowCheckoutModal(false)}
                >
                  Ok
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;